import { Container } from "@mui/material";
import {
  SmartAutoComplete,
  SmartCheckbox,
  SmartForm,
  SmartTextField,
  useSmartForm,
} from "@nodeon/react-components";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  DamDetailsDto,
  MapAreaTypeEnum,
  useMapAreaApi,
  useOrganizationApi,
} from "~/api/GeneratedApi";
import LoadingBox from "~/components/LoadingBox";
import { useCustomQuery } from "~/utils/hooks/useCustomQuery";

export const damDataToForm = (d: DamDetailsDto | undefined): DamFormInputs => {
  return {
    name: d?.name ?? "",
    description: d?.description ?? "",
    active: d?.active ?? true,
    maintainerOrganizationId: d?.maintainerOrganization?.id ?? null,
    mapAreaIds: d?.mapAreas?.map((m) => m.mapAreaId) ?? [],
  };
};

export interface DamFormInputs {
  name: string;
  description: string;
  active: boolean;
  mapAreaIds: number[] | null;
  maintainerOrganizationId: number | null;
}

interface Props {
  onSubmit: (data: DamFormInputs) => void;
  readOnly?: boolean;
  small?: boolean;
  defaultValues?: DamDetailsDto;
  formInitialValues?: DamFormInputs;
  formId: string;
}

const DamForm = ({
  onSubmit,
  readOnly,
  small,
  formId,
  formInitialValues,
  defaultValues,
}: Props): JSX.Element | null => {
  const { t } = useTranslation("dam-page");

  const formDefaultValues = useMemo(
    () => formInitialValues ?? damDataToForm(defaultValues),
    [defaultValues, formInitialValues],
  );

  const formMethods = useSmartForm({
    defaultValues: formDefaultValues,
  });
  const { getOrganizations } = useOrganizationApi();
  const { getMapAreas } = useMapAreaApi();

  const { data: organizationData, isLoading: isLoadingOrgs } = useCustomQuery(
    "organizations",
    () => getOrganizations(),
  );
  const { isLoading: isLoadingAreas, data: mapAreas } = useCustomQuery(
    "map-areas",
    () => getMapAreas(),
  );
  const orgs = useMemo(() => organizationData?.results, [organizationData]);

  if (isLoadingAreas || isLoadingOrgs) return <LoadingBox />;

  return (
    <Container maxWidth={false}>
      <SmartForm<DamFormInputs>
        onSubmit={onSubmit}
        gridProps={{ justifyContent: "center" }}
        methods={formMethods}
        formId={formId}
      >
        <SmartTextField<DamFormInputs>
          name="name"
          label={t("name")}
          gridProps={small ? { xs: 8, lg: 5 } : { xs: 8, lg: 4 }}
          required
          fullWidth
          autoComplete="off"
          InputProps={{ readOnly }}
          disabled={readOnly}
          autoFocus
        />
        <SmartTextField<DamFormInputs>
          name="description"
          label={t("description")}
          gridProps={small ? { xs: 8, lg: 5 } : { xs: 8, lg: 4 }}
          fullWidth
          autoComplete="off"
          InputProps={{ readOnly }}
          disabled={readOnly}
        />
        <SmartCheckbox<DamFormInputs>
          name="active"
          label={t("active")}
          disabled={readOnly}
        />
        <SmartAutoComplete<DamFormInputs, number>
          options={orgs?.map((x) => x.id) ?? []}
          fullWidth
          disabled={isLoadingOrgs || readOnly}
          name="maintainerOrganizationId"
          label={t("maintainer-organization")}
          controllerProps={{
            rules: {
              required: { value: true, message: t("form:required") },
            },
          }}
          gridProps={small ? { xs: 8, lg: 5 } : { xs: 8, lg: 4 }}
          getOptionLabel={(opt) => {
            const org = orgs?.find((x) => x.id === opt);
            return org?.name ?? t("general:unknown");
          }}
        />
        <SmartAutoComplete<DamFormInputs, number, true>
          name="mapAreaIds"
          label={t("map-areas")}
          options={mapAreas?.results.map((x) => x.mapAreaId) ?? []}
          gridProps={{ xs: small ? 11 : 12 }}
          fullWidth
          multiple
          disabled={isLoadingAreas || readOnly}
          getOptionLabel={(opt) => {
            if (!(typeof opt === "number")) return "";
            const ma = mapAreas?.results.find((x) => x.mapAreaId === opt);
            if (!ma) return t("general:unknown");
            return ma.mapAreaType === MapAreaTypeEnum.Project
              ? `${ma.projectID ? `${ma.projectID} -` : ""} ${ma.name}`
              : ma.name;
          }}
        />
      </SmartForm>
    </Container>
  );
};

export default DamForm;
